import {
  ArrowRight,
  Check,
  Crown,
  Flame,
  Gamepad2,
  LockKeyhole,
  Play,
  Sparkles,
  Star,
  Trophy,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import type { Game, GameCategory } from "../../data/games";
import { GameArtwork } from "../GameArtwork/GameArtwork";
import styles from "./GamesView.module.css";

interface GamesViewProps {
  games: readonly Game[];
  onSelectGame: (game: Game, trigger: HTMLButtonElement) => void;
}

type GameFilter = "all" | GameCategory;

interface FilterOption {
  id: GameFilter;
  label: string;
}

interface QuestStop {
  title: string;
  description: string;
  reward: string;
  icon: LucideIcon;
  state: "current" | "next" | "locked";
}

const filters: readonly FilterOption[] = [
  { id: "all", label: "Alla" },
  { id: "party", label: "Fest" },
  { id: "quiz", label: "Quiz" },
  { id: "social", label: "Socialt" },
  { id: "quick", label: "Snabbt" },
];

const questStops: readonly QuestStop[] = [
  {
    title: "Värm upp gänget",
    description: "Starta valfritt spel",
    reward: "+40 XP",
    icon: Play,
    state: "current",
  },
  {
    title: "Fyll soffan",
    description: "Spela med minst 4 vänner",
    reward: "+25 XP",
    icon: Users,
    state: "next",
  },
  {
    title: "Kvällens legend",
    description: "Vinn en hel spelrunda",
    reward: "Ny badge",
    icon: Crown,
    state: "locked",
  },
];

export function GamesView({ games, onSelectGame }: GamesViewProps) {
  const [activeFilter, setActiveFilter] = useState<GameFilter>("all");
  const challengeGame =
    games.find((game) => game.id === "quiz-night") ?? games[0];
  const visibleGames =
    activeFilter === "all"
      ? games
      : games.filter((game) => game.categoryId === activeFilter);

  const handleChallenge = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (challengeGame) {
      onSelectGame(challengeGame, event.currentTarget);
    }
  };

  return (
    <main className={styles.view}>
      <section className={styles.hero} aria-labelledby="games-heading">
        <div className={styles.heroGlow} aria-hidden="true" />
        <div className={styles.confetti} aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>

        <div className={styles.heroTop}>
          <span className={styles.heroEyebrow}>
            <Sparkles aria-hidden="true" />
            Ditt partyäventyr
          </span>
          <span className={styles.streak}>
            <Flame aria-hidden="true" />
            0 dagar
          </span>
        </div>

        <div className={styles.heroCopy}>
          <span className={styles.levelToken} aria-label="Nivå 1">
            <Star aria-hidden="true" />
            <b>1</b>
          </span>
          <div>
            <p>Välkommen, feststartare</p>
            <h1 id="games-heading">Välj nästa uppdrag</h1>
          </div>
        </div>

        <div className={styles.levelProgress}>
          <div>
            <span>Nästa nivå: Isbrytare</span>
            <strong>0 / 100 XP</strong>
          </div>
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-label="Progress mot nivån Isbrytare"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={0}
          >
            <span />
          </div>
        </div>

        <button
          className={styles.challengeButton}
          type="button"
          onClick={handleChallenge}
        >
          <span className={styles.challengeIcon}>
            <Zap aria-hidden="true" />
          </span>
          <span>
            <small>Kvällens bonus · +50 XP</small>
            <strong>Ta dig an Quizkväll</strong>
          </span>
          <ArrowRight aria-hidden="true" />
        </button>
      </section>

      <section className={styles.questSection} aria-labelledby="quest-heading">
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.eyebrow}>Quest 1 av 3</span>
            <h2 id="quest-heading">Vägen till partyproffs</h2>
          </div>
          <span className={styles.rewardPill}>
            <Trophy aria-hidden="true" />
            100 XP
          </span>
        </div>

        <ol className={styles.questPath}>
          {questStops.map((stop, index) => {
            const Icon = stop.icon;

            return (
              <li data-state={stop.state} key={stop.title}>
                <span className={styles.pathLine} aria-hidden="true" />
                <span className={styles.questMarker}>
                  {stop.state === "locked" ? (
                    <LockKeyhole aria-hidden="true" />
                  ) : stop.state === "next" ? (
                    <span>{index + 1}</span>
                  ) : (
                    <Icon aria-hidden="true" />
                  )}
                </span>
                <span className={styles.questCopy}>
                  <strong>{stop.title}</strong>
                  <small>{stop.description}</small>
                </span>
                <span className={styles.questReward}>{stop.reward}</span>
              </li>
            );
          })}
        </ol>
      </section>

      <section className={styles.library} aria-labelledby="library-heading">
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.eyebrow}>Spelvalvet</span>
            <h2 id="library-heading">Hitta ert nästa spel</h2>
          </div>
          <span className={styles.gameCount}>{visibleGames.length} spel</span>
        </div>

        <div className={styles.filters} aria-label="Filtrera spel">
          {filters.map((filter) => (
            <button
              type="button"
              key={filter.id}
              aria-pressed={activeFilter === filter.id}
              onClick={() => setActiveFilter(filter.id)}
            >
              {activeFilter === filter.id ? (
                <Check aria-hidden="true" />
              ) : null}
              {filter.label}
            </button>
          ))}
        </div>

        <div className={styles.gameGrid}>
          {visibleGames.map((game, index) => (
            <button
              className={styles.gameCard}
              type="button"
              key={game.id}
              aria-label={`Öppna detaljer för ${game.title}`}
              onClick={(event) => onSelectGame(game, event.currentTarget)}
            >
              <span className={styles.artworkWrap}>
                <GameArtwork
                  imageUrl={game.imageUrl}
                  title={game.title}
                  fallbackLabel={game.fallbackLabel}
                  categoryId={game.categoryId}
                />
                {index === 0 && activeFilter === "all" ? (
                  <span className={styles.topPick}>
                    <Sparkles aria-hidden="true" />
                    Top pick
                  </span>
                ) : null}
                <span className={styles.playButton}>
                  <Gamepad2 aria-hidden="true" />
                </span>
              </span>
              <span className={styles.cardCopy}>
                <span>
                  <strong>{game.title}</strong>
                  <small>{game.category}</small>
                </span>
                <span className={styles.rating}>
                  <Star aria-hidden="true" />
                  {game.rating.toFixed(1)}
                </span>
              </span>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}
